/**
 * useEntitlementBypass — React hook that reads the Firestore documents
 * needed to determine whether the user is exempt from the low-balance
 * gate (see `entitlementBypass.ts` for the bypass rules).
 *
 * Sources, all watched in real-time:
 *   1. users/{uid}/subscription/subscription          — trial / status / is_client_free
 *   2. users/{uid}                                    — legacy `trial.active`
 *   3. users/{uid}/account_membership/main            — resolves accountId
 *   4. accounts/{accountId}/billing/main              — enterprise plan + status
 *   5. accounts/{accountId}/config/spendLimits        — balance-stop armed?
 *
 * If any of (1) – (5) is missing or unreadable the hook degrades to
 * "no bypass" with the balance stop ARMED — never throws, never blocks the
 * UI thread.
 *
 * The hook is intentionally separate from `useLowBalanceWarning` so
 * that the bypass query is dormant on pages that don't render the
 * webchat input.
 */

import { useEffect, useState } from 'react';
import {
  Unsubscribe,
  doc,
  getFirestore,
  onSnapshot,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  AccountBillingDocLike,
  EntitlementBypassReason,
  SpendLimitsDocLike,
  SubscriptionDocLike,
  UserDocLike,
  deriveBalanceStopArmed,
  deriveEntitlementBypass,
} from './entitlementBypass';

export interface EntitlementBypassState {
  /** True if any bypass condition holds for the current user. */
  hasBypass: boolean;
  /** Which conditions matched (debug / observability). */
  reasons: EntitlementBypassReason[];
  /** True until all five Firestore reads have completed at least once. */
  isLoading: boolean;
  /**
   * Is the acting account's spend balance stop armed? False ONLY when the
   * account is deliberately allowed to run past zero. Distinct from `hasBypass`
   * on purpose: a bypass says "this turn is funded by someone else", while this
   * says "running negative here is not a condition worth reporting at all".
   * Defaults to armed whenever unknown.
   */
  balanceStopArmed: boolean;
}

const DEFAULT_STATE: EntitlementBypassState = {
  hasBypass: false,
  reasons: [],
  isLoading: true,
  balanceStopArmed: true,
};

/**
 * React hook that mirrors the four Firestore docs and computes the
 * bypass state. Fails open (no bypass + not loading) on any error,
 * keeping the existing balance gate behaviour intact for users who
 * genuinely have $0 entitlement.
 */
export function useEntitlementBypass(): EntitlementBypassState {
  const [uid, setUid] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDocLike | null>(
    null
  );
  const [userDoc, setUserDoc] = useState<UserDocLike | null>(null);
  const [accountBilling, setAccountBilling] =
    useState<AccountBillingDocLike | null>(null);
  const [spendLimits, setSpendLimits] = useState<SpendLimitsDocLike | null>(null);

  // Independent "have we received at least one snapshot for this source"
  // flags. We start `loaded = false` for sources that should exist for
  // any logged-in user (subscription, user doc, membership) and mark
  // them loaded as soon as the listener fires (regardless of whether
  // the doc actually exists — `null` is a valid value).
  const [subLoaded, setSubLoaded] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [membershipLoaded, setMembershipLoaded] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [spendLimitsLoaded, setSpendLimitsLoaded] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);

  // Watch Firebase Auth — single source of truth for the active UID.
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (fbUser) => {
      const next =
        fbUser && !fbUser.isAnonymous ? fbUser.uid : null;
      setUid(next);
    });
    return unsub;
  }, []);

  // Reset all sub-state when the UID changes (logout / user swap).
  useEffect(() => {
    setSubscription(null);
    setUserDoc(null);
    setAccountBilling(null);
    setSpendLimits(null);
    setAccountId(null);
    setSubLoaded(false);
    setUserLoaded(false);
    setMembershipLoaded(false);
    setBillingLoaded(false);
    setSpendLimitsLoaded(false);
  }, [uid]);

  // Listener: users/{uid}/subscription/subscription
  useEffect(() => {
    if (!uid) {
      setSubLoaded(true);
      return;
    }
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid, 'subscription', 'subscription');
      unsub = onSnapshot(
        ref,
        (snap) => {
          setSubscription(
            snap.exists() ? (snap.data() as SubscriptionDocLike) : null
          );
          setSubLoaded(true);
        },
        (err) => {
          // Fail open — log and treat as no subscription doc.
          console.warn('[useEntitlementBypass] subscription snapshot error', err);
          setSubscription(null);
          setSubLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useEntitlementBypass] subscription listener setup failed', err);
      setSubscription(null);
      setSubLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid]);

  // Listener: users/{uid} (for legacy `trial.active`)
  useEffect(() => {
    if (!uid) {
      setUserLoaded(true);
      return;
    }
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid);
      unsub = onSnapshot(
        ref,
        (snap) => {
          setUserDoc(
            snap.exists() ? (snap.data() as UserDocLike) : null
          );
          setUserLoaded(true);
        },
        (err) => {
          console.warn('[useEntitlementBypass] user-doc snapshot error', err);
          setUserDoc(null);
          setUserLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useEntitlementBypass] user-doc listener setup failed', err);
      setUserDoc(null);
      setUserLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid]);

  // Listener: users/{uid}/account_membership/main — resolves accountId.
  useEffect(() => {
    if (!uid) {
      setMembershipLoaded(true);
      return;
    }
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(
        getFirestore(getApp()),
        'users',
        uid,
        'account_membership',
        'main'
      );
      unsub = onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists() ? (snap.data() as { accountId?: string }) : null;
          setAccountId(data?.accountId ?? null);
          setMembershipLoaded(true);
        },
        (err) => {
          console.warn('[useEntitlementBypass] membership snapshot error', err);
          setAccountId(null);
          setMembershipLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useEntitlementBypass] membership listener setup failed', err);
      setAccountId(null);
      setMembershipLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid]);

  // Listener: accounts/{accountId}/billing/main — enterprise plan etc.
  useEffect(() => {
    if (!accountId) {
      setAccountBilling(null);
      // We've finished resolving (no account) — mark loaded so the hook
      // doesn't stay perpetually "isLoading: true" for solo users.
      setBillingLoaded(true);
      return;
    }
    setBillingLoaded(false);
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(
        getFirestore(getApp()),
        'accounts',
        accountId,
        'billing',
        'main'
      );
      unsub = onSnapshot(
        ref,
        (snap) => {
          setAccountBilling(
            snap.exists() ? (snap.data() as AccountBillingDocLike) : null
          );
          setBillingLoaded(true);
        },
        (err) => {
          console.warn('[useEntitlementBypass] billing snapshot error', err);
          setAccountBilling(null);
          setBillingLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useEntitlementBypass] billing listener setup failed', err);
      setAccountBilling(null);
      setBillingLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [accountId]);

  // Listener: accounts/{accountId}/config/spendLimits — is the balance stop
  // armed for this workspace? Member-readable by firestore.rules
  // (accounts/{accountId}/config/{docId} allows isAccountMember), which is why
  // this asks the authoritative doc rather than a mirrored copy that could drift
  // from the server's own decision.
  useEffect(() => {
    if (!accountId) {
      setSpendLimits(null);
      setSpendLimitsLoaded(true);
      return;
    }
    setSpendLimitsLoaded(false);
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(
        getFirestore(getApp()),
        'accounts',
        accountId,
        'config',
        'spendLimits'
      );
      unsub = onSnapshot(
        ref,
        (snap) => {
          setSpendLimits(
            snap.exists() ? (snap.data() as SpendLimitsDocLike) : null
          );
          setSpendLimitsLoaded(true);
        },
        (err) => {
          // Fail toward ARMED — the safe direction for a warning.
          console.warn('[useEntitlementBypass] spendLimits snapshot error', err);
          setSpendLimits(null);
          setSpendLimitsLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useEntitlementBypass] spendLimits listener setup failed', err);
      setSpendLimits(null);
      setSpendLimitsLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [accountId]);

  // If there's no UID, we know there's no bypass and nothing to load.
  if (!uid) {
    return { hasBypass: false, reasons: [], isLoading: false, balanceStopArmed: true };
  }

  const isLoading =
    !subLoaded || !userLoaded || !membershipLoaded || !billingLoaded || !spendLimitsLoaded;
  if (isLoading) {
    return DEFAULT_STATE;
  }

  const { hasBypass, reasons } = deriveEntitlementBypass({
    subscription,
    accountBilling,
    userDoc,
  });

  return {
    hasBypass,
    reasons,
    isLoading: false,
    balanceStopArmed: deriveBalanceStopArmed(spendLimits),
  };
}
